import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, BookmarkPlus, Check, ChevronDown, ChevronUp, Clock, Copy, FileText, History, ImageUp,
  Loader2, Mail, Megaphone, Pencil, Plus, Send, Sparkles, Trash2, UserCheck, Users, X,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { upsertFeatureUpdateImage } from "@/lib/feature-update-image-draft";
import {
  MESSAGE_GROUPS, MESSAGE_GROUP_ORDER, blockedRecipientIds, buildSendRequest, centreReducer, cleanSections,
  canSendMessage, createSaveQueue, describeSendResult, hasUnsettledSends, displayName, draftFromSource, filterMessages, initialCentreState,
  isEditable, normaliseMessage, parseMessageType, rowSections, templateFromContent,
  type CentreAction, type CreateMessagePayload, type MessageListKind, type MessageRow, type MessageType,
  type RecipientUser, type RecipientsResponse, type SaveState, type SendHistoryRow, type SendResult, type SectionType,
} from "@/lib/message-centre";
import {
  LegacyFirstQuoteHistory, PreviewCard, RecipientsPanel, SendHistoryTable,
  errorText, formatShortDate, getJson,
} from "@/components/message-centre/shared";

const LIST_KEY = ["/api/admin/messages"];

interface EditorSection { id: string; type: SectionType; content: string }
const newSectionId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const GROUP_ICON = { first_quote: UserCheck, feature_update: Megaphone } as const;

function useMessages() {
  return useQuery<MessageRow[]>({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const d = await getJson<unknown>("/api/admin/messages");
      return Array.isArray(d) ? d.map(r => normaliseMessage(r as MessageRow)) : [];
    },
  });
}

function useCreateMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateMessagePayload) => normaliseMessage(await (await apiRequest("POST", "/api/admin/messages", payload)).json()),
    onSuccess: row => {
      qc.setQueryData<MessageRow[]>(LIST_KEY, old => old ? [row, ...old.filter(r => r.id !== row.id)] : [row]);
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

// ── Composer / editor ────────────────────────────────────────────────────────

function SaveIndicator({ state }: { state: SaveState }) {
  const map: Record<SaveState, string> = { idle: "All changes saved", pending: "Unsaved changes", saving: "Saving", saved: "All changes saved", error: "Save failed, will retry on next change" };
  return (
    <span className={`text-xs inline-flex items-center gap-1.5 ${state === "error" ? "text-destructive" : "text-muted-foreground"}`} data-testid="status-save">
      {state === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className={`h-1.5 w-1.5 rounded-full ${state === "pending" ? "bg-amber-500" : state === "error" ? "bg-destructive" : "bg-emerald-500"}`} />}
      {map[state]}
    </span>
  );
}

export function MessageEditor({
  row, group, selection, dispatch, onBack, onOpen,
}: {
  row: MessageRow;
  group: MessageType;
  selection: Record<string, RecipientUser>;
  dispatch: (a: CentreAction) => void;
  onBack: () => void;
  onOpen: (id: string) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const groupDef = MESSAGE_GROUPS[row.messageType];
  const [unsettled, setUnsettled] = useState(false);
  const [sending, setSending] = useState(false);
  const sendInFlight = useRef(false);
  const contentLocked = !isEditable(row) || unsettled;
  const editable = !contentLocked && !sending;
  // Freeze edits and late AI/upload results while flushing and sending.
  const frozenRef = useRef(false);
  frozenRef.current = !editable;
  const [title, setTitle] = useState(row.title);
  const [templateName, setTemplateName] = useState(row.templateName ?? "");
  const [sections, setSections] = useState<EditorSection[]>(() => rowSections(row).map(s => ({ ...s, id: newSectionId() })));
  const draftRef = useRef({ title, sections, templateName });
  draftRef.current = { title, sections, templateName };
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [lastResult, setLastResult] = useState<SendResult | null>(null);
  const createMessage = useCreateMessage();

  // One serialised queue per editor instance (editor is keyed by row id).
  const queueRef = useRef<ReturnType<typeof createSaveQueue<MessageRow>> | null>(null);
  if (!queueRef.current) {
    const rowId = row.id;
    const messageType = row.messageType;
    const isTemplate = row.isTemplate;
    queueRef.current = createSaveQueue<MessageRow>({
      initial: row,
      save: async () => {
        const d = draftRef.current;
        const payload: Record<string, unknown> = { title: d.title.trim(), sections: cleanSections(d.sections), messageType };
        if (isTemplate) payload.templateName = d.templateName.trim() || "Untitled template";
        return normaliseMessage(await (await apiRequest("PATCH", `/api/admin/messages/${rowId}`, payload)).json());
      },
      onSaved: saved => qc.setQueryData<MessageRow[]>(LIST_KEY, old => old?.map(r => r.id === saved.id ? saved : r)),
      onError: err => toast({ title: "Failed to save", description: errorText(err) || undefined, variant: "destructive" }),
      onState: setSaveState,
    });
  }
  const queue = queueRef.current;
  useEffect(() => () => queue.dispose(), [queue]);

  const touch = () => { if (!frozenRef.current) queue.touch(); };
  const updateSections = (next: EditorSection[]) => {
    if (frozenRef.current) return;
    setSections(next); draftRef.current.sections = next; touch();
  };

  const autoGrowEl = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  };

  // ── Explicit AI controls (feature updates only) ──
  const generateForSection = async (sectionId: string) => {
    setIsGenerating(true);
    try {
      const draftText = draftRef.current.sections.find(s => s.id === sectionId)?.content ?? "";
      const imageUrl = draftRef.current.sections.find(s => s.type === "image" && s.content.trim())?.content;
      const res = await fetch(imageUrl ? "/api/admin/updates/generate-from-image" : "/api/admin/updates/generate-body", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imageUrl
          ? { imageUrl, title: draftRef.current.title, body: draftText }
          : { title: draftRef.current.title, hints: draftText, existingBody: draftText }),
      });
      if (!res.ok) throw new Error("generation failed");
      const { body: generated } = await res.json() as { body: string };
      if (frozenRef.current) throw new Error("frozen");
      updateSections(draftRef.current.sections.map(s => s.id === sectionId ? { ...s, content: generated } : s));
      toast({ title: "Text generated" });
    } catch {
      toast({ title: "Generation failed, try again", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateTitle = async () => {
    setIsGeneratingTitle(true);
    try {
      const bodyText = draftRef.current.sections.filter(s => s.type === "text" && s.content.trim()).map(s => s.content.trim()).join("\n\n");
      const imageUrl = draftRef.current.sections.find(s => s.type === "image" && s.content.trim())?.content;
      const res = await fetch(imageUrl ? "/api/admin/updates/generate-from-image" : "/api/admin/updates/generate-title", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imageUrl ? { imageUrl, title: draftRef.current.title, body: bodyText } : { existingTitle: draftRef.current.title, body: bodyText }),
      });
      if (!res.ok) throw new Error("generation failed");
      const { title: generated } = await res.json() as { title: string };
      if (frozenRef.current) throw new Error("frozen");
      setTitle(generated);
      draftRef.current.title = generated;
      touch();
      toast({ title: "Subject generated" });
    } catch {
      toast({ title: "Generation failed, try again", variant: "destructive" });
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  // ── Image uploads ──
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetRef = useRef<string | null>(null);
  const { uploadFile, isUploading } = useUpload({
    onSuccess: async response => {
      try {
        const markRes = await fetch("/api/admin/updates/make-screenshot-public", {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ objectPath: response.objectPath }),
        });
        if (!markRes.ok) throw new Error("mark-public failed");
        const { publicUrl } = await markRes.json() as { publicUrl: string };
        if (frozenRef.current) throw new Error("frozen");
        const targetId = uploadTargetRef.current;
        uploadTargetRef.current = null;
        updateSections(upsertFeatureUpdateImage(draftRef.current.sections, publicUrl, targetId, newSectionId));
        toast({ title: "Image uploaded" });
      } catch {
        toast({ title: "Image upload failed", variant: "destructive" });
      }
    },
    onError: () => toast({ title: "Image upload failed", variant: "destructive" }),
  });

  // ── History ──
  const { data: history = [], isLoading: historyLoading, isError: historyError, refetch: refetchHistory } = useQuery<SendHistoryRow[]>({
    queryKey: ["/api/admin/messages", row.id, "sends"],
    queryFn: async () => {
      const d = await getJson<unknown>(`/api/admin/messages/${row.id}/sends`);
      return Array.isArray(d) ? (d as SendHistoryRow[]).map(h => ({ ...h, status: h.status ?? "accepted", error: h.error ?? null })) : [];
    },
    enabled: !row.isTemplate,
  });
  const blocked = useMemo(() => blockedRecipientIds(history), [history]);
  useEffect(() => { setUnsettled(hasUnsettledSends(history)); }, [history]);
  const selectedUsers = Object.values(selection);

  // ── Actions ──
  const saveAsTemplate = async () => {
    try {
      await queue.flush();
      const payload = templateFromContent({ title: draftRef.current.title, sections: cleanSections(draftRef.current.sections) }, row.messageType, newTemplateName);
      await createMessage.mutateAsync(payload);
      setTemplateDialog(false);
      setNewTemplateName("");
      toast({ title: "Template saved", description: `"${payload.templateName}" is available under Templates.` });
    } catch (err) {
      toast({ title: "Could not save template", description: errorText(err) || undefined, variant: "destructive" });
    }
  };

  const duplicate = async () => {
    try {
      if (editable) await queue.flush();
      const created = await createMessage.mutateAsync(draftFromSource({ title: draftRef.current.title, sections: cleanSections(draftRef.current.sections) }, row.messageType));
      toast({ title: "Copied to a new draft" });
      onOpen(created.id);
    } catch (err) {
      toast({ title: "Could not duplicate", description: errorText(err) || undefined, variant: "destructive" });
    }
  };

  const send = async () => {
    if (sendInFlight.current || !canSendMessage({ ...row, title, sections: cleanSections(sections) }, false, selectedUsers.length)) return;
    sendInFlight.current = true;
    frozenRef.current = true;
    setSending(true);
    try {
      // Persist the latest composition before sending, without a review dialog.
      const fresh = await queue.flush();
      if (queue.isDirty()) throw new Error("The latest message could not be saved. Please try again.");
      const body = buildSendRequest(fresh, group, selectedUsers.map(u => u.id));
      const res = await apiRequest("POST", `/api/admin/messages/${fresh.id}/send`, body);
      const result = await res.json() as SendResult;
      setLastResult(result);
      dispatch({ type: "clearSelection" });
      toast({ title: "Send finished", description: describeSendResult(result), variant: result.failed || result.uncertain ? "destructive" : undefined });
    } catch (err) {
      toast({ title: "Send not completed", description: errorText(err) || "Nothing was sent.", variant: "destructive" });
    } finally {
      sendInFlight.current = false;
      setSending(false);
      qc.invalidateQueries({ queryKey: ["/api/admin/messages", row.id, "sends"] });
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ["/api/admin/messages/recipients"] });
    }
  };

  const previewSections = cleanSections(sections);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5" data-testid="button-back">
          <ArrowLeft className="h-4 w-4" />{groupDef.label}
        </Button>
        <Badge variant={row.isTemplate ? "outline" : row.status === "sent" ? "default" : "secondary"} data-testid="badge-message-status">
          {row.isTemplate ? "Template" : row.status === "sent" ? "Sent" : "Draft"}
        </Badge>
        {row.autoGenerated && (
          <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950 dark:text-amber-300">
            <Sparkles className="h-3 w-3" />Auto-generated, needs review
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-3">
          {editable && <SaveIndicator state={saveState} />}
        </div>
      </div>

      {contentLocked && (
        <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="notice-sent-locked">
          <span>{unsettled && row.status === "draft" ? "Content is locked while a send is in progress or its outcome is unknown. Check Send history; unconfirmed deliveries are not retried." : "This message has been sent, so its content is locked. You can still send it to other eligible recipients. Duplicate it to change the content."}</span>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={duplicate} disabled={createMessage.isPending} data-testid="button-duplicate-sent">
            <Copy className="h-3.5 w-3.5" />Duplicate to new draft
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{row.isTemplate ? "Template" : "Compose"}</CardTitle>
            <CardDescription className="text-xs">{groupDef.label}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {row.isTemplate && (
              <div className="space-y-1.5">
                <Label className="text-xs">Template name</Label>
                <Input value={templateName} onChange={e => { setTemplateName(e.target.value); draftRef.current.templateName = e.target.value; touch(); }} className="text-sm" data-testid="input-template-name" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Subject</Label>
              <div className="flex gap-2">
                <Input
                  value={title}
                  disabled={!editable}
                  onChange={e => { setTitle(e.target.value); draftRef.current.title = e.target.value; touch(); }}
                  placeholder={row.messageType === "first_quote" ? "e.g. Your first quote takes four minutes" : "e.g. Summary Quote mode is now available"}
                  className="text-sm"
                  data-testid="input-subject"
                />
                {groupDef.aiControls && editable && (
                  <Button
                    type="button" size="sm" variant="outline" className="gap-1.5 shrink-0"
                    disabled={isGeneratingTitle || (!title.trim() && !sections.some(s => s.content.trim()))}
                    onClick={generateTitle}
                    data-testid="button-generate-title"
                  >
                    {isGeneratingTitle ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Generate subject
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Content</Label>
              <div className="space-y-2">
                {sections.map((section, idx) => (
                  <div key={section.id} className="rounded-md border p-2 space-y-1.5" data-testid={`section-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{section.type === "text" ? "Text" : "Image"}</span>
                      {editable && (
                        <div className="flex items-center gap-0.5">
                          <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground" disabled={idx === 0} aria-label="Move up" data-testid={`button-move-up-${idx}`}
                            onClick={() => { const n = [...sections]; [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; updateSections(n); }}>
                            <ChevronUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground" disabled={idx === sections.length - 1} aria-label="Move down" data-testid={`button-move-down-${idx}`}
                            onClick={() => { const n = [...sections]; [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]; updateSections(n); }}>
                            <ChevronDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" aria-label="Remove section" data-testid={`button-remove-section-${idx}`}
                            onClick={() => updateSections(sections.filter((_, i) => i !== idx))}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {section.type === "text" ? (
                      <>
                        <Textarea
                          ref={autoGrowEl}
                          value={section.content}
                          disabled={!editable}
                          onChange={e => { autoGrowEl(e.target); updateSections(sections.map((s, i) => i === idx ? { ...s, content: e.target.value } : s)); }}
                          placeholder="Plain text; line breaks are preserved."
                          className="text-sm min-h-[80px] resize-none overflow-hidden"
                          data-testid={`textarea-section-${idx}`}
                        />
                        {groupDef.aiControls && editable && (
                          <Button type="button" size="sm" variant="outline" className="gap-1.5 h-7 text-xs"
                            disabled={isGenerating || (!section.content.trim() && !title.trim() && !sections.some(s => s.type === "image" && s.content.trim()))}
                            onClick={() => generateForSection(section.id)} data-testid={`button-generate-section-${idx}`}>
                            {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                            {isGenerating ? "Generating" : "Generate description"}
                          </Button>
                        )}
                      </>
                    ) : (
                      <div className="relative w-fit">
                        {section.content ? (
                          <img src={section.content} alt="Section" className="rounded-md border max-h-40 object-contain bg-muted" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No image yet</p>
                        )}
                        {editable && (
                          <Button type="button" size="sm" variant="outline" className="mt-1.5 gap-1.5 h-7 text-xs" disabled={isUploading}
                            onClick={() => { uploadTargetRef.current = section.id; fileInputRef.current?.click(); }} data-testid={`button-replace-image-${idx}`}>
                            <ImageUp className="h-3 w-3" />{section.content ? "Replace image" : "Upload image"}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {editable && (
                <div className="flex items-center gap-2 pt-1">
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" data-testid="button-add-text"
                    onClick={() => updateSections([...sections, { id: newSectionId(), type: "text", content: "" }])}>
                    <Plus className="h-3.5 w-3.5" />Add text
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={isUploading} data-testid="button-add-image"
                    onClick={() => { uploadTargetRef.current = null; fileInputRef.current?.click(); }}>
                    {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageUp className="h-3.5 w-3.5" />}
                    {isUploading ? "Uploading" : "Add image"}
                  </Button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" data-testid="input-image-file"
                onChange={async e => { const f = e.target.files?.[0]; if (f) await uploadFile(f); e.target.value = ""; }} />
            </div>

            <div className="flex gap-2 flex-wrap pt-1 border-t">
              {editable && (
                <Button size="sm" variant="outline" className="gap-1.5 mt-3" disabled={saveState === "saving"} data-testid="button-save"
                  onClick={() => queue.saveNow().then(() => toast({ title: "Saved" })).catch(() => undefined)}>
                  {saveState === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Save {row.isTemplate ? "template" : "draft"}
                </Button>
              )}
              {!row.isTemplate && (
                <Button size="sm" variant="outline" className="gap-1.5 mt-3" onClick={() => { setNewTemplateName(title.trim()); setTemplateDialog(true); }} data-testid="button-save-as-template">
                  <BookmarkPlus className="h-3.5 w-3.5" />Save as template
                </Button>
              )}
              {(editable || row.isTemplate) && (
                <Button size="sm" variant="outline" className="gap-1.5 mt-3" onClick={duplicate} disabled={createMessage.isPending} data-testid="button-duplicate">
                  <Copy className="h-3.5 w-3.5" />{row.isTemplate ? "Use for new draft" : "Duplicate"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <PreviewCard title={title} sections={previewSections} messageType={row.messageType} />
      </div>

      {!row.isTemplate && (
        <Tabs defaultValue={row.status === "sent" ? "history" : "recipients"}>
          <TabsList>
            <TabsTrigger value="recipients" className="gap-1.5" data-testid="tab-recipients"><Users className="h-3.5 w-3.5" />Recipients</TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5" data-testid="tab-history">
              <Clock className="h-3.5 w-3.5" />Send history
              {history.length > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{history.length}</Badge>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="recipients" className="mt-4 space-y-3">
            {lastResult && (
              <div className="rounded-lg border px-4 py-3 text-sm" data-testid="text-send-result">
                <b>Last send:</b> {describeSendResult(lastResult)}.
                {(lastResult.failed > 0 || lastResult.uncertain > 0) && <span className="text-muted-foreground"> Uncertain recipients are blocked from automatic retry; check Send history before contacting them again.</span>}
              </div>
            )}
            <RecipientsPanel
              group={group}
              selection={selection}
              blockedIds={blocked}
              onToggle={u => dispatch({ type: "toggle", user: u, group })}
              onSelectMany={us => dispatch({ type: "selectMany", users: us, group })}
              onDeselectMany={ids => dispatch({ type: "deselectMany", ids })}
              onPrune={ids => dispatch({ type: "prune", ids })}
              onClear={() => dispatch({ type: "clearSelection" })}
              action={
                <Button type="button" size="sm" className="h-7 text-xs gap-1.5" disabled={!canSendMessage({ ...row, title, sections: previewSections }, sending, selectedUsers.length)} onClick={send} data-testid="button-send">
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{sending ? "Sending…" : "Send"}
                </Button>
              }
            />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <SendHistoryTable history={history} isLoading={historyLoading} isError={historyError} onRetry={() => refetchHistory()} />
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as reusable template</DialogTitle>
            <DialogDescription>Stores the current subject and content for {groupDef.label}. This draft is not changed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Template name</Label>
            <Input value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} placeholder="e.g. Spring check-in" data-testid="input-new-template-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog(false)} data-testid="button-cancel-template">Cancel</Button>
            <Button onClick={saveAsTemplate} disabled={!newTemplateName.trim() || createMessage.isPending} className="gap-1.5" data-testid="button-confirm-template">
              {createMessage.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ── Group overview ───────────────────────────────────────────────────────────

function MessageList({ rows, kind, onOpen, onDelete, onUse }: {
  rows: MessageRow[];
  kind: MessageListKind;
  onOpen: (id: string) => void;
  onDelete: (r: MessageRow) => void;
  onUse?: (r: MessageRow) => void;
}) {
  if (rows.length === 0) {
    const copy = { drafts: "No drafts. Create a blank draft or use one of your saved templates.", sent: "Nothing sent from this group yet.", templates: "No saved templates. Use Save as template from any draft." }[kind];
    return <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">{copy}</div>;
  }
  return (
    <div className="rounded-lg border divide-y bg-card">
      {rows.map(r => (
        <div key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors" data-testid={`row-message-${r.id}`}>
          <button type="button" className="flex-1 min-w-0 text-left" onClick={() => onOpen(r.id)} data-testid={`button-open-${r.id}`}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{kind === "templates" ? (r.templateName || "Untitled template") : (r.title || "(no subject)")}</span>
              {r.autoGenerated && kind === "drafts" && <Badge variant="outline" className="text-[10px] gap-1 text-amber-700 border-amber-300"><Sparkles className="h-2.5 w-2.5" />Auto</Badge>}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {kind === "templates" ? r.title : `${kind === "sent" ? "Sent" : "Edited"} ${formatShortDate(r.updatedAt || r.createdAt)}`}
            </div>
          </button>
          {onUse && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onUse(r)} data-testid={`button-use-${r.id}`}>
              {kind === "sent" ? <Copy className="h-3 w-3" /> : <FileText className="h-3 w-3" />}{kind === "sent" ? "Duplicate" : "Use"}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onOpen(r.id)} aria-label="Open" data-testid={`button-edit-${r.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {kind !== "sent" && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => onDelete(r)} aria-label="Delete" data-testid={`button-delete-${r.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AdminUpdatesPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const urlGroup = parseMessageType(params.get("type"), "feature_update");
  const preselectId = params.get("userId");
  const basePath = location.startsWith("/admin/updates") ? "/admin/updates" : "/admin/messages";

  const [state, dispatch] = useReducer(centreReducer, urlGroup, g => initialCentreState(g));
  const group = state.group;
  useEffect(() => { dispatch({ type: "switchGroup", group: urlGroup }); }, [urlGroup]);

  const switchGroup = (g: MessageType) => {
    dispatch({ type: "switchGroup", group: g });
    setLocation(`${basePath}?type=${g}`);
  };

  // Preselect a user linked from All Users (only if the server lists them as eligible).
  const preselectDone = useRef<string | null>(null);
  const { data: preData, isFetched: preFetched } = useQuery<RecipientsResponse>({
    queryKey: ["/api/admin/messages/recipients", "first_quote", "preselect", preselectId],
    queryFn: () => getJson<RecipientsResponse>(`/api/admin/messages/recipients?type=first_quote&page=1&pageSize=5&search=${encodeURIComponent(preselectId ?? "")}`),
    enabled: !!preselectId && group === "first_quote",
  });
  const preUser = preData?.users?.find(u => u.id === preselectId);
  useEffect(() => {
    if (preUser && preselectDone.current !== preUser.id && group === "first_quote") {
      preselectDone.current = preUser.id;
      dispatch({ type: "selectMany", users: [preUser], group: "first_quote" });
    }
  }, [preUser, group]);

  const { data: messages = [], isLoading, isError, refetch } = useMessages();
  const createMessage = useCreateMessage();
  const [deleteTarget, setDeleteTarget] = useState<MessageRow | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/messages/${id}`),
    onSuccess: (_d, id) => {
      qc.setQueryData<MessageRow[]>(LIST_KEY, old => old?.filter(r => r.id !== id));
      qc.invalidateQueries({ queryKey: LIST_KEY });
      setDeleteTarget(null);
      toast({ title: "Deleted" });
    },
    onError: err => toast({ title: "Failed to delete", description: errorText(err) || undefined, variant: "destructive" }),
  });

  const autoDraftMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/updates/auto-draft")).json() as Promise<{ title: string }>,
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      toast({ title: "Auto-draft created", description: `"${data.title}" added as a Feature Updates draft.` });
    },
    onError: err => toast({ title: errorText(err) || "Failed to generate draft", variant: "destructive" }),
  });

  const startDraft = async (source: { title: string; sections: { type: SectionType; content: string }[] } | null) => {
    try {
      const created = await createMessage.mutateAsync(
        source ? draftFromSource(source, group) : { title: "", sections: [{ type: "text", content: "" }], messageType: group, isTemplate: false },
      );
      dispatch({ type: "open", id: created.id });
    } catch (err) {
      toast({ title: "Could not create draft", description: errorText(err) || undefined, variant: "destructive" });
    }
  };

  const groupDef = MESSAGE_GROUPS[group];
  const drafts = filterMessages(messages, group, "drafts");
  const sent = filterMessages(messages, group, "sent");
  const templates = filterMessages(messages, group, "templates");
  const editingRow = state.editingId ? messages.find(m => m.id === state.editingId) : undefined;
  // Never render an editor whose row belongs to another group.
  const editorRow = editingRow && editingRow.messageType === group ? editingRow : undefined;
  const lastOpenedMessage = useRef<string | null>(null);
  const audienceSendInFlight = useRef(false);
  useEffect(() => {
    if (editorRow) lastOpenedMessage.current = editorRow.id;
  }, [editorRow?.id]);
  const audienceSend = useMutation({
    mutationFn: async () => {
      const message = messages.find(m => m.id === lastOpenedMessage.current && m.messageType === group && !m.isTemplate)
        ?? (drafts.length === 1 ? drafts[0] : undefined);
      if (!message) throw new Error("Open the message you want to send first, then select its recipients and click Send.");
      const recipients = Object.values(state.selection).filter(u => u.email && !u.emailUpdatesOptOut);
      if (!canSendMessage(message, false, recipients.length)) throw new Error("Complete the message and select eligible recipients before sending.");
      const response = await apiRequest("POST", `/api/admin/messages/${message.id}/send`,
        buildSendRequest(message, group, recipients.map(u => u.id)));
      return response.json() as Promise<SendResult>;
    },
    onSuccess: result => {
      dispatch({ type: "clearSelection" });
      toast({ title: "Send finished", description: describeSendResult(result), variant: result.failed || result.uncertain ? "destructive" : undefined });
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ["/api/admin/messages"] });
    },
    onError: err => toast({ title: "Send not completed", description: errorText(err) || "Nothing was sent.", variant: "destructive" }),
    onSettled: () => { audienceSendInFlight.current = false; },
  });

  const counts = (t: MessageType) => ({
    drafts: filterMessages(messages, t, "drafts").length,
    sent: filterMessages(messages, t, "sent").length,
  });

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold" data-testid="text-page-title">Message Centre</h1>
          <p className="text-muted-foreground text-sm">Compose customer emails, select your recipients and click Send.</p>
        </div>
      </div>

      {/* Group selector */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Message group">
        {MESSAGE_GROUP_ORDER.map(t => {
          const def = MESSAGE_GROUPS[t];
          const Icon = GROUP_ICON[t];
          const active = t === group;
          const c = counts(t);
          return (
            <button
              key={t}
              type="button"
              aria-pressed={active}
              title={def.audience}
              onClick={() => switchGroup(t)}
              className={`flex w-36 max-w-full flex-col items-center gap-2 rounded-lg border px-3 py-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${active ? "border-primary bg-primary/10 ring-1 ring-primary" : "hover:border-primary/40 hover:bg-muted/40"}`}
              data-testid={`button-group-${t}`}
            >
              <div className={`rounded-md p-2 ${active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}><Icon aria-hidden="true" className="h-5 w-5" /></div>
              <span className="text-xs font-semibold leading-4">{def.label}</span>
              <span className="text-[10px] text-muted-foreground tabular-nums">{isLoading ? "Loading" : `${c.drafts} draft${c.drafts !== 1 ? "s" : ""}, ${c.sent} sent`}</span>
            </button>
          );
        })}
      </div>

      {preselectId && group === "first_quote" && preFetched && !preUser && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950 px-4 py-3 text-sm text-amber-900 dark:text-amber-200" data-testid="notice-preselect-ineligible">
          The user linked from All Users is not currently eligible for First Quote Messages, so they were not selected.
        </div>
      )}
      {preUser && state.selection[preUser.id] && !editorRow && (
        <div className="rounded-lg border px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="notice-preselected">
          <span><b>{displayName(preUser) || preUser.email}</b> is selected. Open a draft or start one from a template to send.</span>
        </div>
      )}

      {editorRow ? (
        <MessageEditor
          key={editorRow.id}
          row={editorRow}
          group={group}
          selection={state.selection}
          dispatch={dispatch}
          onBack={() => dispatch({ type: "open", id: null })}
          onOpen={id => dispatch({ type: "open", id })}
        />
      ) : isError ? (
        <Card><CardContent className="py-10 text-center space-y-2">
          <p className="text-sm text-muted-foreground">Could not load messages.</p>
          <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="button-retry-messages">Retry</Button>
        </CardContent></Card>
      ) : (
        <div key={group} className="space-y-6 animate-in fade-in-0 slide-in-from-bottom-1 duration-300">
          <div>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-sm font-semibold flex items-center gap-2"><Mail className="h-4 w-4" />{groupDef.label}</h2>
                <div className="flex gap-2 flex-wrap">
                  {groupDef.autoDraft && (
                    <Button size="sm" variant="outline" onClick={() => autoDraftMutation.mutate()} disabled={autoDraftMutation.isPending} className="gap-1.5" data-testid="button-auto-draft">
                      {autoDraftMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      Draft from recent commits
                    </Button>
                  )}
                  <Button size="sm" onClick={() => startDraft(null)} disabled={createMessage.isPending} className="gap-1.5" data-testid="button-new-message">
                    <Plus className="h-3.5 w-3.5" />Blank draft
                  </Button>
                </div>
              </div>
              {isLoading ? (
                <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
              ) : (
                <Tabs defaultValue="drafts">
                  <TabsList>
                    <TabsTrigger value="drafts" data-testid="tab-drafts">Drafts <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{drafts.length}</span></TabsTrigger>
                    <TabsTrigger value="sent" data-testid="tab-sent">Sent <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{sent.length}</span></TabsTrigger>
                    <TabsTrigger value="templates" data-testid="tab-templates">Templates <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{templates.length}</span></TabsTrigger>
                    {groupDef.legacyHistory && <TabsTrigger value="legacy" className="gap-1" data-testid="tab-legacy"><History className="h-3 w-3" />Legacy</TabsTrigger>}
                  </TabsList>
                  <TabsContent value="drafts" className="mt-3">
                    <MessageList rows={drafts} kind="drafts" onOpen={id => dispatch({ type: "open", id })} onDelete={setDeleteTarget} />
                  </TabsContent>
                  <TabsContent value="sent" className="mt-3">
                    <MessageList rows={sent} kind="sent" onOpen={id => dispatch({ type: "open", id })} onDelete={setDeleteTarget}
                      onUse={r => startDraft({ title: r.title, sections: rowSections(r) })} />
                  </TabsContent>
                  <TabsContent value="templates" className="mt-3">
                    <MessageList rows={templates} kind="templates" onOpen={id => dispatch({ type: "open", id })} onDelete={setDeleteTarget}
                      onUse={r => startDraft({ title: r.title, sections: rowSections(r) })} />
                  </TabsContent>
                  {groupDef.legacyHistory && (
                    <TabsContent value="legacy" className="mt-3"><LegacyFirstQuoteHistory /></TabsContent>
                  )}
                </Tabs>
              )}
            </div>

          </div>

          <RecipientsPanel
            group={group}
            selection={state.selection}
            onToggle={u => dispatch({ type: "toggle", user: u, group })}
            onSelectMany={us => dispatch({ type: "selectMany", users: us, group })}
            onDeselectMany={ids => dispatch({ type: "deselectMany", ids })}
            onPrune={ids => dispatch({ type: "prune", ids })}
            onClear={() => dispatch({ type: "clearSelection" })}
            action={
              <Button type="button" size="sm" className="h-7 text-xs gap-1.5"
                disabled={audienceSend.isPending || Object.keys(state.selection).length === 0}
                onClick={() => {
                  if (audienceSendInFlight.current) return;
                  audienceSendInFlight.current = true;
                  audienceSend.mutate();
                }}
                data-testid="button-audience-send">
                {audienceSend.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {audienceSend.isPending ? "Sending…" : "Send"}
              </Button>
            }
          />
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.isTemplate ? "template" : "draft"}?</DialogTitle>
            <DialogDescription>"{deleteTarget?.isTemplate ? deleteTarget?.templateName : deleteTarget?.title || "(no subject)"}" will be removed. Sent messages cannot be deleted.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="button-cancel-delete">Cancel</Button>
            <Button variant="destructive" onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending} data-testid="button-confirm-delete">
              {deleteMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
